/* eslint-env browser */

import RiotControl from 'riotcontrol'
import riotRoute from 'riot-route'
import cookie from 'cookie-cutter'
import OnDemandLiveRegion from 'on-demand-live-region'
import logger from '../logger'
import {
  merge,
  filter,
  indexOf,
  isEmpty,
  get,
  isFunction,
  findIndex,
} from 'lodash-es'
import extendedObservable from './base'
import routesDef from '../routes-def'
import routeStates from '../route-states'
import UserUtils from '#util/utils/user-utils'
import { OrderIssues, Errors } from '#util/utils/error-utils'
import AppOverlayService from '../services/app/app-overlay.service'
import BootstrapUtils from '#util/utils/bootstrap-utils'
import RouteRedirect from '../route-redirect'
import appProps from '#util/app-props'
import AnalyticsStrings from '#util/metrics/strings.js'
import PIEUtils from '#util/utils/pie-utils'

import { TestsObservableInstance } from '#util/observables/tests-observable'

//  Progress Cookie Key.  Chose this name so that it is not obvious what this value does
//  so that users will not alter it.
const ProgressKey = 'opProg'

const ConfirmationPageUID = 'confirmation'
const ConfirmationHeaderLabel = 'Thank you, '
const ConfirmationHeaderLabelSuffix = '!'

/**
 * Sets window.rei_checkout for Qualtrics "Value from JavaScript" embedded data
 * (survey bucket + order id for customer identification) and registers a one-shot
 * listener for the Qualtrics survey display event. Called after Adobe Target resolves
 * so the CHEX158 bucket is known. All users assigned to this bucket are treated as Control.
 */
const initQualtricsEmbeddedData = (orderModel) => {
  const surveyBucket = 'checkout:order confirmation_control';

  window.rei_checkout = {
    survey: surveyBucket,
    orderId: orderModel?.safeGet('externalOrderId') || '',
  };

  document.addEventListener('qualtrics:survey-displayed', () => {
    RiotControl.trigger('analytics.sendClick', {
      linkName: `${surveyBucket}_survey launch`,
      pageName: 'checkout:order confirmation',
    });
  }, { once: true });
};


// /////////////////////
// Application State //
// /////////////////////



class ApplicationObservable {
  constructor() {
    extendedObservable(this, 'appModel')
  
    this.name = 'applicationObservable'
  
    this.showMemberOnlyModal = false

    this.available_sections = {
      membership: {
        uid: 'membership',
        route: 'membership',
        title: 'REI Co-op Membership',
        subtitle: '',
        navTitle: 'Membership',
        isVisibleStep: true,
        nextCopy: 'Add your REI membership',
        onClickBackAnalyticsInfo: 'membership: back to cart',
      },
      'contact-billing': {
        uid: 'contact-billing',
        route: 'contact-billing',
        title: 'Contact and Billing',
        subtitle: '',
        navTitle: 'Contact and Billing',
        isVisibleStep: true,
        nextCopy: 'Next, add your contact and billing information',
        onClickBackAnalyticsInfo: 'membership: back to previous step',
      },
      shipping: {
        uid: 'shipping',
        route: 'shipping-options',
        title: 'Items to ship',
        subtitle: 'You can review store pickup items in the next step.',
        navTitle: 'Shipping Options',
        isVisibleStep: true,
        nextCopy: 'Next, review your shipping options.',
        onClickBackAnalyticsInfo: 'shipping: back to previous step',
      },
      pickup: {
        uid: 'pickup',
        route: 'store-pickup',
        title: 'Store pickup items',
        subtitle: 'We’ll email you when your item is ready to be picked up.',
        navTitle: 'Store Pickup',
        isVisibleStep: true,
        nextCopy: 'Next, review your store pickup items.',
        onClickBackAnalyticsInfo: 'pickup: back to previous step',
      },
      summary: {
        uid: 'summary',
        route: 'order-summary',
        title: 'Review your order',
        subtitle: 'Please review the following information for accuracy.',
        navTitle: 'Review',
        isVisibleStep: true,
        nextCopy: 'Next, review your order and add payment',
        onClickBackAnalyticsInfo: 'summary: back to previous step',
      },
      confirmation: {
        uid: 'confirmation',
        route: 'order-confirmation',
        title: 'Thank You!',
        subtitle: '',
        navTitle: 'confirmation',
        isVisibleStep: false,
        nextCopy: '',
        onClickBackAnalyticsInfo: '',
      },
      'confirmation-ap': {
        uid: 'confirmation-ap',
        route: 'order-confirmation?ref=ap',
        title: 'Thank You!',
        subtitle: '',
        navTitle: 'confirmation',
        isVisibleStep: false,
        nextCopy: '',
        onClickBackAnalyticsInfo: '',
      },
      transition: {
        uid: 'transition',
        route: 'loading',
        title: '',
        subtitle: '',
        navTitle: 'loading',
        isVisibleStep: false,
        nextCopy: '',
        onClickBackAnalyticsInfo: '',
      },
    };
    
    this.pagesOrder = [
      'membership',
      'contact-billing',
      'shipping',
      'pickup',
      'summary',
      'confirmation',
      'confirmation-ap',
    ];
    
    this.model = merge( this.model, {
      currentIndex: 0,
      currentStep: 0,
      currentPageUid: null,
      externalOrderId: null,
      orderRetrieved: false,
      orderAudited: false,
      isInEditMode: false,
      editSection: null,
      useShippingAsBilling: true,
      globalMessage: {},
      available_sections: this.available_sections,
      isAuditRequired: false,
      disableNavHandler: false,
      willBootstrap: false,
      isInitPhase: false,
      skipAuditOnTransition: false,
      isFetchingFulfillments: false,
      fulfillmentsSuccess: false,
      fulfillmentsFailure: false,
      isFetchingShippingCharge: false,
      shippingChargeSuccess: false,
      shippingChargeFailure: false,
      usersNameDomNode: null,
      isUpdatingLineItems: false,
      isApplePayConfirmation: false,
      hasRemovedMembership: false,
      appProps: appProps.getAll(),
      activeTests: {},
      adobeInitCalled: false,
      requestsInFlight: false,
      paidWithKlarna: false,
      testCCEnabled: false,
      liveRegion: new OnDemandLiveRegion({ level: 'assertive' }),
      sameBillingAndShipping: false,
    })
  
  
    Object.defineProperty(this.model, 'totalSteps', {
      get: () => {
        const mapped = this.pagesOrder.map(pageUid => this.available_sections[pageUid])
        const filtered = filter(mapped, (o) => {
          // find the index of the iterator in the `pagesOrder` property
          const ind = indexOf(this.pagesOrder, o.uid)

          // check to see if current index was found and `isVisibleStep`
          return ind >= 0 && o.isVisibleStep
        }).length

        return this.model.orderRetrieved ? filtered : '-'
      },
      configurable: true,
    })

    Object.defineProperty(this.model, 'sections', {
      get: () => {
        const mapped = this.pagesOrder.map(pageUid => this.available_sections[pageUid])
        const result = filter(mapped, (o) => {
          const result1 = indexOf(this.pagesOrder, o.uid) >= 0 && o.isVisibleStep
          return result1
        })

        return result
      },
      configurable: true,
    })

    Object.defineProperty(this.model, 'getAppProp', {
      value: (name, defaultValue) => {
        if (!this.model.appProps[name]) {
          return defaultValue
        }

        return this.model.appProps[name]
      },
      configurable: true,
    })

    // register and unregister event listeners
    this.on('registerApplicationStateObserver', observer => this.registerObserver(observer))
    this.on('unregisterApplicationStateObserver', observer => this.unregisterObserver(observer))

    // Fulfillments Events
    this.on('fetchingFulfillments', () => {
      Object.assign(this.model, {
        isFetchingFulfillments: true,
        fulfillmentsSuccess: false,
        fulfillmentsFailure: false,
      })
    })

    this.on('fetchingFulfillmentsComplete', () => Object.assign(this.model, { isFetchingFulfillments: false }))

    this.on('fetchingFulfillmentsSuccess', () => {
      Object.assign(this.model, {
        isFetchingFulfillments: false,
        fulfillmentsSuccess: true,
        fulfillmentsFailure: false,
      })
    })

    this.on('fetchingFulfillmentsFailure', () => {
      Object.assign(this.model, {
        isFetchingFulfillments: false,
        fulfillmentsSuccess: false,
        fulfillmentsFailure: true,
      })
    })


    // Shipping Charges Events
    this.on('fetchingShippingCharge', () => {
      Object.assign(this.model, {
        isFetchingShippingCharge: true,
        shippingChargeSuccess: false,
        shippingChargeFailure: false,
      })
    })

    this.on('fetchingShippingChargeComplete', () => {
      Object.assign(this.model, { isFetchingShippingCharge: false })
    })

    this.on('shippingChargeSuccess', () => {
      Object.assign(this.model, {
        isFetchingShippingCharge: false,
        shippingChargeSuccess: true,
        shippingChargeFailure: false,
      })
    })

    this.on('shippingChargeFailure', () => {
      Object.assign(this.model, {
        isFetchingShippingCharge: false,
        shippingChargeSuccess: false,
        shippingChargeFailure: true,
      })
    })

    // Line Item Events
    this.on('updateLineItemsWithAddress', () => {
      Object.assign(this.model, {
        isUpdatingLineItems: true,
        updatingLineItemsSuccess: false,
        updatingLineItemsFailure: false,
      })
    })

    this.on('updateLineItemsSuccess', () => {
      Object.assign(this.model, {
        isUpdatingLineItems: false,
        updatingLineItemsSuccess: true,
        updatingLineItemsFailure: false,
      })
    })

    this.on('updateLineItemsFailure', () => {
      Object.assign(this.model, {
        isUpdatingLineItems: false,
        updatingLineItemsSuccess: false,
        updatingLineItemsFailure: true,
      })
    })

    this.on('updateAppModelManually', (property, data) => {
      Object.assign(this.model[property], data)
    })

    this.on('coupon-details-link', (data) => {
      this.displayCouponDetailsModal(data)
    })

    this.on('member-coupon-requirements-link', (data) => {
      this.displayMemberCouponRequirementsModal(data)
    })

    this.on('updateAppModel', (property, value) => {
      this.model[property] = value
    })

    this.on('orderModelManuallySet', (data) => {
      this.orderModel = data
      this.notifyObservers()
    })

    this.on('applicationInit', () => {
      logger('applicationInit', 'debug')()
      RiotControl.trigger('registerTestsObserver', ApplicationObservableInstance)

      // short-circuit for Apple Pay redirect
      if (window.location.href.match(/ref=ap/)) {
        this.model.isInitPhase = false
        this.model.isApplePayConfirmation = true
        this.model.skipAuditOnTransition = true

        const keyName = 'reiOrderModel'

        const persistedOrderModel = JSON.parse(localStorage.getItem(keyName))
        if (!isEmpty(persistedOrderModel)) {
          // Delete the order information after is has been retrieved.
          localStorage.removeItem(keyName)
          // Manually set the retrieved order to the order observable
          RiotControl.trigger('manuallySetOrderModel', persistedOrderModel)
        } else {
          // They don't appear to have an order but are on the
          // confirmation page. Send them to the cart
          window.location = '/ShoppingCart'
        }

        this.model.adobeInitCalled = true
        TestsObservableInstance.checkToInitAdobe(this.orderModel)
          .then(() => initQualtricsEmbeddedData(this.orderModel))
          .then(() => {
            this.notifyObservers({ event: 'applicationInitComplete' })
          })
      } else {
        RiotControl.trigger('fetchCountries')
        if (appProps.get('payment.saved.gc.enabled', false)) RiotControl.trigger('getGiftCards')
        RiotControl.trigger('requestOrder', requestOrderSuccess, requestOrderFailure)
      }

      // Only check for scripts if the app prop is on
      const encryptionEnabled = appProps.get('chase.end-to-end-encryption.enabled', false)
      if (encryptionEnabled) {
        // Check for e2e encryption scripts
        const status = checkChaseE2EScripts()
        if (status === false) {
          logger('ERROR: Problem downloading REI hosted checkChaseE2EScripts... trying backup', 'error')()
          displayNoEncryptionModal()
        }
      }

      window.addEventListener('beforeunload', () => { this.clearProgress() })

      // There is sometimes a race condition in production where `google` is not
      // defined but we are trying to initialize the autocomplete. This checks to
      // see if the callback has been called from the loading of the google script.
      // If this variable isn't set, then google maps api callback has not fired.
      if (appProps.get('use.google.autocomplete', false) && !document.reiGmapReady) {
        document.addEventListener('rei.gmap.ready', () => {
          this.notifyObservers({ event: 'gmapReady' })
        })
      }

      this.notifyObservers()
    })
    

    const requestOrderSuccess = (order) => {
      this.orderModel = order
      this.model.isInitPhase = true

      // Kind of a hack ಠ_ಠ
      // On page load on the order-summary page we need to call audit with EADs. However as it
      // is routerInit, which determines the page we are on, requires audit to be complete...
      // As such we have this.
      // eslint-disable-next-line
      if (window.location.href.match(/order-summary/)) RiotControl.trigger('auditOrderWithEADs');
      else RiotControl.trigger('auditOrder')

      if (this.orderModel.safeGet('getters.hasFulfillmentRestrictions')) {
        this.model.isInEditMode = true
        riotRoute('/shipping-options')
      }
    }

    const requestOrderFailure = (failed) => {
      const errors = new Errors(get(failed, 'responseJSON', {}))
      if (errors.hasCode([
        Errors.codes.INVALID_COOKIE,
        Errors.codes.OP_ORDER_NOT_FOUND,
      ])) {
        // They have a bad orderid. get a good one.
        window.location = '/ShoppingCart'
      }
    }

    this.globalOverlayQueue = []
    this.globalOverlayTimer = null
    this.globalOverlayTimeout = 15000

    // Event Listeners for global overlay. Prefer to have here than on the component.
    // Additionally, need a way to create an overlay queue since n number of
    // calls can come into `showGlobalOverlay` we should keep it open until all
    // of the "work" is complete.
    this.on('showGlobalOverlay', () => {
      if (this.model.isInitPhase && this.globalOverlayQueue.length) return
      clearTimeout(this.globalOverlayTimer)
      this.globalOverlayTimer = null

      // Safety Net. If the spinner is open for
      // more than this.globalOverlayTimeout, close it.
      // resets on each successive call to showGlobalOverlay
      this.globalOverlayTimer = setTimeout(() => {
        RiotControl.trigger('hideGlobalOverlay', () => {
          logger(`ERROR :: (GLOBAL OVERLAY) Forcing closure due to hitting timeout of ${this.globalOverlayTimeout}ms`, 'error')()
        }, {}, true)
      }, this.globalOverlayTimeout)

      if (!this.globalOverlayQueue.length) {
        AppOverlayService.show()
      }
      this.globalOverlayQueue.push('')
    })

    this.on('hideGlobalOverlay', (cb, options, force) => {
      if (this.model.isInitPhase) return
      if (force === true) this.globalOverlayQueue = []
      else this.globalOverlayQueue.shift()

      if (this.globalOverlayQueue.length === 0) {
        clearTimeout(this.globalOverlayTimer)
        this.globalOverlayTimer = null
        AppOverlayService.hide()
          .then(() => new Promise((resolve) => {
            if (isFunction(cb)) {
              cb(options)
            }
            return resolve()
          }))
      }
    })

    this.on('pageTransition', (uid) => {
      logger('pageTransition event', 'debug')()

      const index = findIndex(this.model.sections, { uid })

      // Current Unique Id of the page
      this.model.currentPageUid = uid

      // Current page index in the array
      this.model.currentIndex = index

      // This is used to show the user the step they are on out of the total
      this.model.currentStep = index + 1

      // Set the <title>
      const pageName = this.available_sections[this.model.currentPageUid]?.title;
      const pageTitle = `REI.com Checkout${pageName ? ` - ${pageName}` : ''}`;
      document.title = pageTitle
      this.model.liveRegion.say(`${pageTitle} page`)

      this.model.isInitPhase = false
      this.model.skipAuditOnTransition = false
      if (!this.model.isApplePayConfirmation) RiotControl.trigger('hideGlobalOverlay')

      // Triggering a global message without a message clears it out.
      // TODO: This is dumb. Implement better (clearer) way to do this.
      //       I would expect triggering a global message with no content
      //       to display a global message with no content, not hide it.
      RiotControl.trigger('globalMessage')

      if (uid === 'confirmation') {
        const firstName = this.orderModel.safeGet('billingAddress.address.firstName')
        // Update the section data to include user's firstname.
        if (firstName) {
          const ref = get(this.model.available_sections, ConfirmationPageUID)
          if (ref && !isEmpty(ref)) {
            ref.title =
              ConfirmationHeaderLabel +
              firstName +
              ConfirmationHeaderLabelSuffix
          }
        }
      }
      this.notifyObservers()
    })

    this.on('userReachedReviewPage', () => {
      this.model.userReachedReviewPage = true
      this.notifyObservers()
    })

    // Used to control behavior when users use the
    // edit links on the review and submit page.
    this.on('notifyEditMode', (isInEditMode, notifier, editSection) => {
      logger('notifyEditMode triggered', 'debug')()
      this.model.editSection = isInEditMode ? editSection : null
      if (isInEditMode) {
        logger(`${notifier} called edit`, 'debug')()
        this.model.isInEditMode = true
      } else {
        logger('Turning off \'editMode\'', 'debug')()
        this.model.isInEditMode = false
      }
      this.notifyObservers()
    })

    // Handler to trigger the global level messaging
    // in the application
    this.on('globalMessage', (mesg = {}) => {
      this.model.globalMessage = mesg
      this.notifyObservers()
    })

    // Handler to set state on order retrieved.
    this.on('order.retrieved', () => {
      this.model.orderRetrieved = true
    })

    // TODO: It would be great to check for out of stock
    // items as soon as the application is ready (once an
    // order is retrieved)
    this.on('order.audited', (data) => {
      this.model.orderAudited = true

      // TODO: we need to add a way to hook into out of stock items
      // so that we can notify the user appropriately.
      // code 107 === 'out of stock'
      // sample path data.fulfillments[0].shipMethodsInformation.restrictions[0].code === 107
      this.orderModel = data

      if (this.model.isInitPhase && !this.model.adobeInitCalled) {
        this.model.adobeInitCalled = true
        TestsObservableInstance.checkToInitAdobe(this.orderModel)
          .then(() => initQualtricsEmbeddedData(this.orderModel))
          .then(auditCascade)
          .then(() => {
            this.notifyObservers({ event: 'applicationInitComplete' })
            checkForBadRoute() // Check after the router has been initialized and page has been determined
          })
      } else {
        auditCascade()
      }
    })

    const auditCascade = () => {
      // calculate the order of the pages based upon the data coming back
      this.pagesOrder = this.calculatePagesOrder()

      // Current page index in the array
      const index = findIndex(this.model.sections, { uid: this.model.currentPageUid })
      this.model.currentIndex = index

      // This is used to show the user the step they are on out of the total
      this.model.currentStep = index + 1

      if (this.model.currentIndex !== 0) this.checkForMembership()

      // checks to see if the order contains member
      // only items without membership
      checkForMemberOnlyItems()

      // check to see if the order has more than one
      // billing address (order merge)
      checkForMultipleBillingAddresses()

      // check to see if the order has more than one
      // shipping address (not including stores)
      checkForMultipleShippingAddresses()

      // Check to see if the page you are on is valid (eg
      // visible to user based on `calculatePagesOrder`).
      // If not, move them somewhere else
      checkForBadRoute()

      // notify observers of any changes.
      this.notifyObservers()
    }


    // ////////////////////
    // Checkout Progress //
    // ////////////////////

    //  Saves Checkout progress in cookie in case user manually navigates by the url
    //  or browser nagigation controls.
    this.updateProgress = (state) => {
      logger(`CHECKOUT PROGRESS UPDATE = ${state.id}`, 'warning')()
      if (state && state.id) {
        const date = new Date()
        date.setDate(date.getDate() + 1)
        cookie.set(ProgressKey, state.id, { expires: date })
      }
    }

    this.getProgress = () => {
      let value = -1
      const state = parseInt(cookie.get(ProgressKey), 10)
      if (!isNaN(state)) value = state
      return value
    }

    this.clearProgress = () => {
      const progressFound = this.getProgress()
      if (progressFound > -1) cookie.set(ProgressKey, -1, { expires: new Date(0) })
    }

    // Update Progress State for better navigation/route control
    this.on('updateBillingProgress', () => this.updateProgress(routeStates.contactBilling))
    this.on('updateContactProgress', () => {
      if (this.getProgress() < routeStates.storePickup.id) {
        this.updateProgress(routeStates.shippingOptions)
      }
    })
    this.on('updateShippingProgress', () => this.updateProgress(routeStates.storePickup))
    this.on('updateStorePickupProgress', () => this.updateProgress(routeStates.storePickup))
    this.on('updateOrderSubmitProgress', () => this.updateProgress(routeStates.orderConfirmation))

    // ----------------------------- //


    /*
      Check to see if the order has blocking order issues
      and if so, return a path for user to address
      the issues. Used in Router.
      */
    this.hasBlockingOrderIssues = () => {
      const orderIssues = new OrderIssues(this.orderModel.safeGet('orderIssues'))

      if (orderIssues.hasIncompleteBillingAddress()
        || orderIssues.hasInvaildBillingAddressCharacters()) {
        return routesDef.contactBilling
      } else if (orderIssues.hasInvaildShipAddressCharacters()
        || orderIssues.hasNoShipMethod()
        || orderIssues.hasNoShipAddress()
        || orderIssues.hasShippingAddressConflictsWithPickup()) {
        return routesDef.shippingOptions
      } else if (orderIssues.isAlreadyAMember()) {
        this.updateProgress(routeStates.storePickup)
        return routesDef.orderSummary
      }

      return false
    }


    /**
     * Dynamic route calculation when app is initialized.
     * Called from the router. If a user lands on `/checkout`
     * OR an unknown route, this method will be called.
     * @return {String}         route to land user on
     */
    this.calculateInitialRoute = () => {
      const defaultRoute = routesDef.contactBilling
      this.model.disableNavHandler = true

      return Promise.resolve()
        .then(() => {
          const orderIssues = this.orderModel.safeGet('orderIssues')

          // if there are no order issues at all, just head to orderSummary
          // TODO: is this block ever even used? I don't think we
          // ever fast-forward to orderSummary...
          if (!orderIssues || orderIssues.length === 0) {
            RiotControl.trigger('auditOrderWithEADs')
            return routesDef.orderSummary
          }

          // snoop to see if an email swap happened
          // TODO: this is not really the right place to
          // be checking for order issues. I think this was
          // put here because we only want to show this modal
          // on startup, but this should be moved to a better
          // location at some point.
          const emailUpdated = this.orderModel.safeGet('orderIssues', []).some(issue => issue.code === 606)
          if (emailUpdated) {
            this.displayChangedEmailModal()
          }

          // ////////////////////////
          // Application Bootstrap //
          // ////////////////////////

          // Show the overlay for the bootstrapping.
          RiotControl.trigger('showGlobalOverlay')

          // Return promise of bootstrap which will ultimately
          // return the route string user should go to
          const bootstrapUtils = new BootstrapUtils(this.model)
          return bootstrapUtils.bootstrapApplication(this.orderModel)
            .catch((err) => {
              // If the error thrown is an instance of RouteRedirect,
              // then we know this is an exception to redirect the
              // user instead of an unhandled exception.
              if (err instanceof RouteRedirect) {
                logger(`WARNING :: (APP BOOTSTRAP) user being redirected to ${err.route}`, 'warning')()
                return err.route
              }

              // unhandled error
              logger(`ERROR :: unhandled error ${err}`, 'error')()

              // return default route so application can recover
              return defaultRoute
            })
        })
        .then((route) => {
          const hasViewedMembership = cookie.get('confirmedNonMember') === this.orderModel.safeGet('orderId')
          const isMember = UserUtils.isMember(this.orderModel.safeGet('customerInfo'))

          RiotControl.trigger('hideGlobalOverlay')
          this.model.disableNavHandler = false
          this.model.willBootstrap = false

          // If this user is in the membership page test, skip the membership page
          const skipMembership = TestsObservableInstance.getTestVariant('CCX1921') === 'C'

          // If you are bucketed into C, are not a member, and have not opted to skip
          // membership in the past then you would "normally" see membership, so we
          // need to fire the eligibleTests variable to signify you are in the test.
          if (skipMembership && !isMember && !hasViewedMembership) {
            RiotControl.trigger('analytics.sendClick', { eligibleTests: 'Checkout_MembershipPageMods' })
          }

          // if a non member has not viewed the membership page route them there
          if (!skipMembership && !isMember && !hasViewedMembership) return routesDef.membership

          return route
        })
    }

    this.model.getCurrentIndex = id => findIndex(this.model.sections, { uid: id })

    this.model.nextPageIsSummary = () => {
      const nextSection = this.model.sections[this.model.getCurrentIndex(this.model.currentPageUid) + 1]

      if (!nextSection) return false // no next section found so it cant be summary
      if (this.model.isInEditMode) return true // edit mode means next page is the summary
      return get(nextSection, 'uid') === 'summary' // finally check if the summary is the next page
    }

    /**
     * Method to potentially route the user if the page they are on is no longer valid
     * @return {none}         No return
     */
    const checkForBadRoute = () => {
      if (this.model.disableNavHandler) return
      // Page not in the dynamic flow
      if (this.model.currentIndex === -1) {
        // eslint-disable-next-line max-len
        const curIndexInAvailSections = findIndex(this.model.sections, { uid: this.model.currentPageUid })

        // get previousSection
        const previousSection = this.model.sections[curIndexInAvailSections - 1] || {}

        // Check to see if the new route is a good one
        const newIndex = findIndex(this.model.sections, { uid: previousSection.uid })

        if (newIndex === -1) {
          // could not find index. Go to first page.
          if (!this.model.isInitPhase) riotRoute(routesDef.membership)
        } else if (newIndex > 0) {
          // Go back one index. This should guarantee a valid route since there will
          const newRoute = previousSection.route
          riotRoute(`/${newRoute}`)
        } else {
          // if the previous section is also bad, step back two. This should guarantee
          // that we're on a good page because the max possible "bad" pages is two
          // if you were to remove both ship and pickup items and only have a membership
          // eslint-disable-next-line max-len
          const previousPreviousSection = this.model.sections[curIndexInAvailSections - 1]
          const newNewRoute = previousPreviousSection.route
          riotRoute(`/${newNewRoute}`)
        }
      }
    }

    /**
     * Method to take calculate which pages should be included in the
     * user flow based upon information in the order.
     * @return {none}         No return
     */
    this.calculatePagesOrder = () => {
      // base page order. These pages should always display.
      const splicePosition = 2
      const pagesOrder = [
        'membership',
        'contact-billing',
        // 'shipping' might go here,
        // 'pickup' might go here,
        'summary',
        'confirmation',
      ]

      // If order contains pickup, add
      // Do this check first so that the shipping
      // section will also be spliced into index 3
      // (eg. placed before this)
      if (this.orderModel.safeGet('getters.filteredPickupLineItems', []).length > 0) pagesOrder.splice(splicePosition, 0, 'pickup')

      // If order contains shipping, add
      // NOTE: If there is only a membership, shipping is skipped since memberships
      // "ship" to billing address. `filteredShipItems` should not return a membership.
      if (this.orderModel.safeGet('getters.filteredShipLineItems', []).length > 0
        || this.orderModel.safeGet('getters.filteredPhysicalGiftLineItems', []).length > 0) {
        pagesOrder.splice(splicePosition, 0, 'shipping')
      }

      // return
      return pagesOrder
    }

    // /////////////
    // Membership //
    // TODO: Move this stuff
    // /////////////

    this.checkForMembership = () => {
      // Find if membership is in cart or not
      const memberships = this.orderModel.safeGet('getters.filteredMembershipCardItems')
      // Is primary member?
      const isPrimaryMember = UserUtils.isPrimaryMember(this.orderModel.safeGet('customerInfo'))
      const orderIssues = new OrderIssues(this.orderModel.safeGet('orderIssues'))

      if (orderIssues.isAlreadyAMember() || (memberships.length && isPrimaryMember && !this.deletingMembership)) {
        this.deleteMembership(memberships)
      } else if (memberships.length > 1 && !this.deletingMembership) {
        this.deletingMembership = true
        RiotControl.trigger('deleteItems', memberships.slice(1), () => {
          RiotControl.trigger('hideGlobalOverlay')
          this.deletingMembership = false
          RiotControl.trigger('globalMessage', {
            content: `We're sorry, but you cannot order more than one REI membership online.
                      We’ve removed the additional membership(s) from your cart.
                      To purchase a gift membership, please call <a href="tel:1-800-426-4840">1-800-426-4840</a> or visit your local REI store.`,
          })
        })
      }
    }

    this.deleteMembership = (items) => {
      this.deletingMembership = true
      // Delete Item(s)
      RiotControl.trigger('deleteItems', items, () => {
        // If all they have in their order is a membership then they will have an empty cart,
        // redirect them to the cart page instead of doing audit.
        if (this.orderModel.safeGet('lineItems', []).length === 1) {
          window.location.href = '/ShoppingCart?error_code=emptyCartMembershipsRemoved'
        } else {
          this.deletingMembership = false
          // hasRemovedMembership is to show a message on the review page, it stays true
          this.model.hasRemovedMembership = true
          // hasRemovedMembership is for analytics, it's used to set a flag in the next pageview
          // event and then is set to false, it's only supposed to fire with the next pageview.
          this.model.membershipAutoRemoved = true
          RiotControl.trigger('globalMessage', {
            content: `<b>Our records indicate that you're already an REI Co-op Member.</b>
            We removed the extra membership(s) from your cart.
            <a href='https://www.rei.com/membership/lookup'>Visit our membership lookup and FAQ page</a> for help, or call Customer Support.
            To purchase a gift membership, please call Customer Support at <a href='tel:1-800-426-4840'>1-800-426-4840</a>, or visit your local REI store.`,
          })
          RiotControl.trigger('auditOrder')
          RiotControl.trigger('hideGlobalOverlay')
        }
      })
    }

    this.removeMemberOnlyItems = (memberOnlyItems) => {
      RiotControl.trigger('deleteItems', memberOnlyItems, () => {
        this.showMemberOnlyModal = false
        RiotControl.trigger('globalMessage', { content: 'Just for members product(s) were removed from your cart.' })
        RiotControl.trigger('auditOrder')
      })
    }

    // -------------------------- //


    // /////////
    // Modals //
    // TODO: decouple and move these
    // /////////

    // Method used in members-only-modal to handle user selection when a JFM item is in cart without a membership
    const handleChoice = (option, memberOnlyItems) => {
      if (option === 'already') {
        riotRoute(routesDef.membership)
        this.showMemberOnlyModal = false
      } else if (option === 'add') {
        RiotControl.trigger('addMembership')
      } else if (option === 'remove') {
        this.removeMemberOnlyItems(memberOnlyItems)
      }
    }
    const pageName = () => AnalyticsStrings.page[this.model.currentPageUid]

    this.displayMemberOnlyModal = (memberOnlyItems) => {
      RiotControl.trigger('modalFactory.create', {
        title: 'You need to be an REI member to purchase one of the items in your cart.',
        width: 600,
        noClose: true,
        template: 'member-only-modal',
        buttons: [{
          title: 'Continue',
          el_classes: 'btn btn-secondary',
          on_click: (option) => {
            handleChoice(option, memberOnlyItems)
          },
          on_keypress: (option) => {
            handleChoice(option, memberOnlyItems)
          },
        }],
        onClose: (option) => {
          handleChoice(option, memberOnlyItems)
          RiotControl.trigger('analytics.sendClick', {
            linkName: AnalyticsStrings.link.membersOnlyModal('continue', pageName()),
          })
        },
      })
    }

    this.displayMultiShipToModal = () => {
      RiotControl.trigger('modalFactory.create', {
        title: 'Your order contains multiple shipping addresses — orders can be shipped to only one address.',
        width: 600,
        noClose: true,
        template: 'select-shipping-address-modal',
        buttons: [{
          title: 'Continue and verify shipping method',
          el_classes: 'btn btn-primary',
          on_click: payload =>
            RiotControl.trigger('updateLineItemsWithAddress', payload,
              () => riotRoute(routesDef.shippingOptions),
              () => riotRoute(routesDef.shippingOptions)
            ),
          on_keypress: payload =>
            RiotControl.trigger('updateLineItemsWithAddress', payload,
              () => riotRoute(routesDef.shippingOptions),
              () => riotRoute(routesDef.shippingOptions)
            ),
        }],
      })
    }

    this.displayMultiBillToModal = () => {
      RiotControl.trigger('modalFactory.create', {
        title: 'Confirm your billing address',
        noClose: true,
        template: 'select-billing-address-modal',
        buttons: [{
          title: 'Continue with this address',
          el_classes: 'btn btn-primary',
          on_click: (payload) => {
            if (payload === 'billingAddress') {
              RiotControl.trigger('setBillingAddressCookie')
            } else if (payload === 'savedBillingAddress') {
              const savedBilling = this.orderModel.safeGet('user.savedBillingAddress')

              if (savedBilling.address.scrubStatus === 'SCRUBBED_ADDRESS'
                || savedBilling.address.scrubStatus === 'USER_ADDRESS') {
                RiotControl.trigger('updateBillingAddress', savedBilling)
              } else {
                // We're going to trigger the address scrubbing modal and we only allow one modal
                // at a time so we need to kill this one first to prevent a race condition.
                RiotControl.trigger('clearModals', () => {
                  RiotControl.trigger('scrubAddressWithModal', savedBilling.address,
                  // If they select one of the address options in the USPS validation modal
                    scrubbed => RiotControl.trigger('updateBillingAddress', scrubbed),
                    // If the chose to edit their address through the USPS validation modal
                    (scrubbed) => {
                      this.model.skipAuditOnTransition = true
                      RiotControl.trigger('updateOrderResource', 'billingAddress', scrubbed)
                      riotRoute(routesDef.contactBilling)
                    })
                })
              }
            }
          },
        }],
      })
    }

    this.displayChangedEmailModal = () => {
      RiotControl.trigger('modalFactory.create', {
        title: 'Your email address has been updated to',
        template: 'invalid-contact-info-modal',
        data: {
          email: this.orderModel.safeGet('billingAddress.address.email', null),
        },
        buttons: [{
          title: 'Got it!',
          el_classes: 'btn btn-primary',
          on_click: () => {},
          on_keypress: () => {},
        }],
      })
    }

    this.displayCouponDetailsModal = (data) => {
      RiotControl.trigger('modalFactory.create', {
        title: 'Coupon terms and conditions',
        template: 'coupon-terms-conditions-modal',
        data,
      })
    }

    this.displayMemberCouponRequirementsModal = (data) => {
      RiotControl.trigger('modalFactory.create', {
        title: 'Member coupon requirements',
        template: 'member-coupon-requirements-modal',
        data,
      })
    }

    const displayNoEncryptionModal = () => {
      RiotControl.trigger('modalFactory.create', {
        title: 'Error: Service unavailable',
        template: 'e2e-service-error-modal',
        width: 400,
        buttons: [{
          title: 'Keep shopping',
          el_classes: 'btn btn-primary',
          on_click: () => {
            window.location = '/'; // eslint-disable-line
          },
        }],
      })
    }

    /**
     * Checks to see if there are member only items on the order
     * and the user is a member. If not, displays a modal to the user.
     * @return {none} no return
     */
    const checkForMemberOnlyItems = () => {
      const isMember = this.orderModel.safeGet('getters.filteredMembershipCardItems').length || UserUtils.isMember(this.orderModel.safeGet('customerInfo'))
      const memberOnlyItems = this.orderModel.safeGet('getters.hasMemberOnlyItems')
      if (!this.showMemberOnlyModal && this.model.currentIndex !== 0 && memberOnlyItems.length && !isMember) {
        this.showMemberOnlyModal = true
        this.displayMemberOnlyModal(memberOnlyItems)
      }
    }

    /**
     * Checks to see if there are multiple billing addresses on
     * the order (usually the result of a cart merge). If there
     * are, displays a modal for the user to select the correct
     * address.
     * @return {none} no return
     */
    const checkForMultipleBillingAddresses = () => {
      const hasMultipleBillingAddresses = this.orderModel.safeGet('getters.hasMultipleBillingAddresses')
      const hasConfirmedBilling = cookie.get('useOrderBilling') === 'true'
      if (hasMultipleBillingAddresses && !hasConfirmedBilling) {
        this.displayMultiBillToModal()
      }
    }

    /**
     * Checks to see if there are multiple shipping addresses on
     * the order. If there are, displays a modal for the user to
     * select the correct address.
     * @return {none} no return
     */
    const checkForMultipleShippingAddresses = () => {
      const multiShipToItems = this.orderModel.safeGet('getters.getItemsWithUniqShipToAddresses')
      if (multiShipToItems.length > 1) {
        this.displayMultiShipToModal()
      }
    }

    /**
     * Checks for a download error of the e2e encryption scripts
     * @return {Boolean} true/false. true indicates there IS a download issue
     */
    const checkChaseE2EScripts = () =>
      PIEUtils.isPieKeyScriptDownloaded() &&
      PIEUtils.isPieEncryptionScriptDownloaded()
  }
}

export const ApplicationObservableInstance = new ApplicationObservable();
